import React, { useEffect } from 'react';
import { 
  ArrowLeft,
  DeviceMobile,
  Download,
  Star,
  Wrench,
  Question
} from '@phosphor-icons/react';
import { 
  Box,
  Typography,
  Container,
  IconButton,
  Paper,
  Grid,
  Card,
  CardContent,
  CardHeader,
  Divider
} from '@mui/material';
import { PageContainer, Content } from '../PageTemplate';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';

const GradientHero = styled.div`
  background: linear-gradient(
    150deg,
    #000000 0%,
    #1a1a1a 50%,
    #000000 100%
  );
  background-size: 200% 200%;
  animation: gradientMove 15s ease infinite;
  padding: 6rem 2rem 3rem 2rem;
  margin-top: -80px;
  color: white;
  text-align: center;
  position: relative;
  overflow: hidden;

  @keyframes gradientMove {
    0% { background-position: 0% 50%; }
    50% { background-position: 100% 50%; }
    100% { background-position: 0% 50%; }
  }
`;

const BackButton = styled(IconButton)`
  position: absolute;
  left: 24px;
  top: 24px;
  color: white;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  z-index: 10;
  
  &:hover {
    background: rgba(255, 255, 255, 0.2);
  }
`;

const HeroContent = styled(motion.div)`
  position: relative;
  z-index: 2;
  padding-top: 2rem;
`;

const SectionCard = styled(Card)`
  height: 100%;
  transition: transform 0.2s ease-in-out;
  background: rgba(255, 255, 255, 0.98);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(229, 231, 235, 0.5);
  cursor: pointer;
  
  &:hover {
    transform: translateY(-4px);
  }
`;

const HelpCenterIOS = () => {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const sections = [
    {
      icon: <Download size={32} weight="duotone" />,
      title: "Getting Started",
      description: "Learn how to download, install, and set up the ServTrackr iOS app",
      path: "/resources/help-center/ios/getting-started"
    },
    {
      icon: <Wrench size={32} weight="duotone" />,
      title: "Troubleshooting",
      description: "Solutions to common issues and problems with ServTrackr iOS",
      path: "/resources/help-center/ios/troubleshooting"
    },
    {
      icon: <Question size={32} weight="duotone" />,
      title: "FAQ",
      description: "Find answers to the most common questions about ServTrackr iOS",
      path: "/resources/help-center/ios/faq"
    }
  ];

  return (
    <PageContainer>
      <GradientHero>
        <BackButton onClick={() => navigate('/resources/help-center')}>
          <ArrowLeft size={24} />
        </BackButton>
        <HeroContent
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <DeviceMobile size={64} weight="duotone" style={{ marginBottom: '1rem' }} />
          <motion.h1
            style={{
              fontSize: '3.5rem',
              marginBottom: '1.5rem',
              fontWeight: 700,
              position: 'relative',
              zIndex: 2
            }}
          >
            iOS Help Center
          </motion.h1>
          <motion.p
            style={{
              fontSize: '1.25rem',
              maxWidth: '800px',
              margin: '0 auto',
              opacity: 0.9,
              lineHeight: 1.6,
              position: 'relative',
              zIndex: 2
            }}
          >
            Everything you need to know about using ServTrackr on iOS
          </motion.p>
        </HeroContent>
      </GradientHero>

      <Content>
        <Container maxWidth="lg">
          <Grid container spacing={3}>
            {sections.map((section, index) => (
              <Grid item xs={12} md={4} key={index}>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                >
                  <SectionCard onClick={() => navigate(section.path)}>
                    <CardHeader
                      avatar={section.icon}
                      title={section.title}
                      titleTypographyProps={{ variant: 'h6' }}
                    />
                    <Divider />
                    <CardContent>
                      <Typography variant="body1" color="text.secondary">
                        {section.description}
                      </Typography>
                    </CardContent>
                  </SectionCard>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Content>
    </PageContainer>
  );
};

export default HelpCenterIOS;
